import { Outlet, useNavigate } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarHeaderButton,
  SidebarHeading,
  SidebarItem,
  SidebarLink,
  SidebarList,
  SidebarOrganizationMenu,
  SidebarTrigger,
  SidebarUserMenu,
} from '@/app/ui/components/sidebar';
import { Main } from '@/app/ui/components/main';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  AiBrain02Icon,
  ArrowDownIcon,
  ArrowTurnBackwardIcon,
  ArrowUpIcon,
  Building06Icon,
  FileBookmarkIcon,
  InboxIcon,
  Search01Icon,
  SettingsIcon,
  SparklesIcon,
  Task02Icon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import {
  BottomNavigation,
  BottomNavigationLink,
  BottomNavigationList,
} from '@/app/ui/components/bottom-navigation';
import { useTranslations } from 'use-intl';
import * as React from 'react';
import {
  Command,
  CommandCollection,
  CommandDialog,
  CommandDialogPopup,
  CommandDialogTrigger,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from '@/app/ui/components/command';
import { useGetProjectsList } from '@/app/hooks/queries/useProjects';
import { useContext } from 'react';
import { AppContext } from '@/app/appContext';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useOrganizationKnowledgeBases } from '@/app/hooks/queries/useInternalKnowledgeBases';
import { useOrganizationTenderFeedbacks } from '@/app/hooks/queries/useStandaloneTenderFeedbacks';
import { Folder02Icon } from '@hugeicons/core-free-icons';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/app/ui/components/tooltip';
import { Kbd, KbdCommand, KbdGroup } from '@/app/ui/components/kbd';
import { NewIntercomMessenger } from '@/app/components/intercom/NewIntercomMessenger';
import { Badge } from '@/app/ui/components/badge';
import { FavoritesList } from '@/app/components/features/favorites/FavoritesList';

export const Route = createFileRoute('/_authed/(organization)/_layout')({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages');
  const tGeneral = useTranslations('general');
  const tComponents = useTranslations('components');
  const { userMeta } = useContext(AppContext);

  return (
    <div className="flex grow min-w-0 bg-background">
      {userMeta && <NewIntercomMessenger loggedUser={userMeta} />}
      <Sidebar>
        <SidebarContent>
          <SidebarHeader>
            <SidebarOrganizationMenu />
            <Search />
            <SidebarTrigger />
          </SidebarHeader>
          <SidebarList>
            <SidebarItem>
              <SidebarLink to={'/'}>
                <HugeiconsIcon icon={Building06Icon} />
                {t('homepage.breadcrumbs.projects')}
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink
                to={'/notifications'}
                onClick={(e) => e.preventDefault()}
                className="hover:bg-transparent cursor-default"
              >
                <HugeiconsIcon icon={InboxIcon} />
                {t('notifications.title')}
                <Badge size="xs" variant="tertiary" className="ml-auto -mr-0.75 text-muted">
                  {tGeneral('comingSoon')}
                </Badge>
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink
                to={'/tasks'}
                onClick={(e) => e.preventDefault()}
                className="hover:bg-transparent cursor-default"
              >
                <HugeiconsIcon icon={Task02Icon} />
                {t('tasks.title')}
                <Badge size="xs" variant="tertiary" className="ml-auto -mr-0.75 text-muted">
                  {tGeneral('comingSoon')}
                </Badge>
              </SidebarLink>
            </SidebarItem>
          </SidebarList>
          <SidebarHeading>{tComponents('orgSidebar.yourOrg')}</SidebarHeading>
          <SidebarList>
            <SidebarItem>
              <SidebarLink to={'/learnings'}>
                <HugeiconsIcon icon={SparklesIcon} />
                {t('learnings.title')}
                <Badge size="xs" className="ml-auto -mr-0.75">
                  {tGeneral('beta')}
                </Badge>
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink to={'/knowledge-base'}>
                <HugeiconsIcon icon={AiBrain02Icon} />
                {t('knowledgeBase.breadcrumbs.knowledgeBase')}
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink to={'/templates'}>
                <HugeiconsIcon icon={FileBookmarkIcon} />
                {t('templates.breadcrumbs.templates')}
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink to={'/members'}>
                <HugeiconsIcon icon={UserGroupIcon} />
                {t('teamMembers.title')}
              </SidebarLink>
            </SidebarItem>
            <SidebarItem>
              <SidebarLink to={'/settings'}>
                <HugeiconsIcon icon={SettingsIcon} />
                {t('organizationSettings.breadcrumbs.settings')}
              </SidebarLink>
            </SidebarItem>
          </SidebarList>
          <SidebarHeading>{tComponents('projectSidebar.favorites')}</SidebarHeading>
          <FavoritesList />
          <SidebarUserMenu />
        </SidebarContent>
      </Sidebar>
      <Main>
        <Outlet />
      </Main>
      <BottomNavigation>
        <BottomNavigationList className="grid-cols-5">
          <BottomNavigationLink to={'/'}>
            <HugeiconsIcon icon={Building06Icon} />
            {t('homepage.breadcrumbs.projects')}
          </BottomNavigationLink>
          <BottomNavigationLink to={'/learnings'}>
            <HugeiconsIcon icon={SparklesIcon} />
            {t('learnings.title')}
          </BottomNavigationLink>
          <BottomNavigationLink to={'/knowledge-base'}>
            <HugeiconsIcon icon={AiBrain02Icon} />
            {t('knowledgeBase.breadcrumbs.knowledgeBaseShort')}
          </BottomNavigationLink>
          <BottomNavigationLink to={'/templates'}>
            <HugeiconsIcon icon={FileBookmarkIcon} />
            {t('templates.breadcrumbs.templates')}
          </BottomNavigationLink>
          <BottomNavigationLink to={'/members'}>
            <HugeiconsIcon icon={UserGroupIcon} />
            {t('teamMembers.titleShort')}
          </BottomNavigationLink>
        </BottomNavigationList>
      </BottomNavigation>
    </div>
  );
}

type SearchItem = {
  value: string;
  label: string;
  type: 'project' | 'knowledge-base' | 'tender-feedback';
  onClick: () => void;
};

type SearchGroup = {
  value: string;
  items: SearchItem[];
};

function Search() {
  const t = useTranslations();
  const navigate = useNavigate();
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta.organizationId ?? '';
  const [open, setOpen] = React.useState(false);
  const { data: projects } = useGetProjectsList(organizationId, '', 999);
  const { data: knowledgeBases } = useOrganizationKnowledgeBases(organizationId);
  useOrganizationTenderFeedbacks(organizationId);
  const [searchText, setSearchText] = React.useState('');

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const searchGroups: SearchGroup[] = React.useMemo(() => {
    const groups: SearchGroup[] = [];

    if (projects && projects.length > 0) {
      groups.push({
        value: 'projects',
        items: projects.map((project) => ({
          value: `project-${project.id}`,
          label: project.name,
          type: 'project',
          onClick: () => {
            navigate({ to: '/project/$projectId', params: { projectId: project.id } });
            setOpen(false);
          },
        })),
      });
    }

    if (knowledgeBases && knowledgeBases.length > 0) {
      groups.push({
        value: 'knowledge-bases',
        items: knowledgeBases.map((kb) => ({
          value: `kb-${kb.id}`,
          label: kb.name,
          type: 'knowledge-base',
          onClick: () => {
            navigate({
              to: '/knowledge-base/internal/$knowledgeBaseId',
              params: { knowledgeBaseId: kb.id },
            });
            setOpen(false);
          },
        })),
      });
    }

    return groups;
  }, [projects, knowledgeBases, navigate]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <CommandDialogTrigger
              render={
                <SidebarHeaderButton
                  aria-label="search"
                  className="ml-auto"
                  onClick={() => setOpen(true)}
                >
                  <HugeiconsIcon icon={Search01Icon} />
                </SidebarHeaderButton>
              }
            />
          }
        />
        <TooltipPopup>
          {t('components.globalSearch.search')}{' '}
          <KbdGroup>
            <KbdCommand />
            <Kbd>K</Kbd>
          </KbdGroup>
        </TooltipPopup>
      </Tooltip>
      <CommandDialogPopup>
        <Command items={searchGroups}>
          <div className="relative">
            <CommandInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('components.globalSearch.searchProjects')}
            />
            <KbdGroup className="absolute inset-y-0 my-auto right-3">
              <KbdCommand />
              <Kbd>K</Kbd>
            </KbdGroup>
          </div>
          <CommandPanel>
            <CommandEmpty className={'break-all'}>
              {t('components.globalSearch.noResults', { searchText })}
            </CommandEmpty>
            <CommandList>
              {(group: SearchGroup) => (
                <React.Fragment key={group.value}>
                  <CommandGroup items={group.items}>
                    <CommandGroupLabel>
                      {group.value === 'projects' && t('pages.homepage.breadcrumbs.projects')}
                      {group.value === 'knowledge-bases' &&
                        t('pages.knowledgeBase.breadcrumbs.knowledgeBase')}
                      {group.value === 'tender-feedbacks' && t('pages.tenderFeedbacks.title')}
                    </CommandGroupLabel>
                    <CommandCollection>
                      {(item: SearchItem) => {
                        const project = projects?.find((p) => `project-${p.id}` === item.value);
                        return (
                          <CommandItem key={item.value} onClick={item.onClick} value={item.value}>
                            {item.type === 'project' && project && (
                              <ProjectTypeIcon type={project.type} />
                            )}
                            {item.type === 'knowledge-base' && (
                              <HugeiconsIcon icon={Folder02Icon} />
                            )}

                            <span className="line-clamp-1">{item.label}</span>
                          </CommandItem>
                        );
                      }}
                    </CommandCollection>
                  </CommandGroup>
                </React.Fragment>
              )}
            </CommandList>
          </CommandPanel>
          <CommandFooter>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <KbdGroup>
                  <Kbd>
                    <HugeiconsIcon icon={ArrowUpIcon} />
                  </Kbd>
                  <Kbd>
                    <HugeiconsIcon icon={ArrowDownIcon} />
                  </Kbd>
                </KbdGroup>
                <span>{t('general.navigate')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Kbd>
                  <HugeiconsIcon icon={ArrowTurnBackwardIcon} />
                </Kbd>
                <span>{t('general.open')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Kbd>Esc</Kbd>
              <span>{t('general.close')}</span>
            </div>
          </CommandFooter>
        </Command>
      </CommandDialogPopup>
    </CommandDialog>
  );
}
